// Mobile background audio — the orchestrator (testable core).
//
// SUMMED-STREAM MODEL (2026-07-24 rework). iOS freezes ALL JS of a
// backgrounded/locked tab and suspends Web Audio; only a plain <audio> element
// that was ALREADY playing keeps sounding. The earlier "hand off on
// visibilitychange" model could not work: (a) starting <audio> on hide has no
// user gesture, so WebKit blocks play(); (b) JS is frozen the instant the tab
// hides, so a hide-triggered start never runs. Confirmed against WebKit's
// MediaElementSession::canShowControlsManager + playbackStateChangePermitted.
//
// So on MOBILE the background <audio> is the SOLE audible source from the first
// Play tap onward — no hand-off at all:
//   • The mix is pre-rendered to one stereo WAV and kept armed on the <audio>.
//   • `onPlayGesture()` is called SYNCHRONOUSLY from the Play control's click
//     handler; it starts the (pre-armed) <audio> inside the user gesture — the
//     only moment WebKit permits play() — and SILENCES the live Web Audio engine
//     so there's no double audio. The live engine keeps running for the visual
//     playhead/waveform only (muted).
//   • Media Session (lock-screen) controls drive the <audio> directly.
//   • Nothing happens on visibilitychange — the <audio> keeps playing in the
//     background natively, which is the whole point.
//
// On DESKTOP the live Web Audio engine stays primary (desktop tabs aren't frozen
// like iOS), and the orchestrator is just a thin Media Session wiring over it —
// no background render, no <audio>, no silencing.
//
// Everything is injected so the wiring is unit-testable without a real player or
// browser.

import type { Player } from '../player.js';
import type { MixInput } from './mix-renderer.js';
import type { BackgroundAudio } from './background-audio.js';
import type { wireMediaSession, setPositionState, setPlaybackState } from './media-session.js';

export interface BackgroundPlaybackDeps {
  player: Pick<
    Player,
    'getMixSnapshot' | 'getDuration' | 'getPlayhead' | 'seek' | 'play' | 'pause' | 'isPlaying' | 'subscribe' | 'setSilent'
  >;
  backgroundAudio: BackgroundAudio;
  renderToBlob: (inputs: MixInput[], opts: { sampleRate: number; durationSec: number }) => Promise<Blob>;
  wireMediaSession: typeof wireMediaSession;
  setPositionState: typeof setPositionState;
  setPlaybackState: typeof setPlaybackState;
  sampleRate: number;
  metadata: { title: string; artist?: string; album?: string; artwork?: Array<{ src: string; sizes?: string; type?: string }> };
  /**
   * True on touch phones/tablets, where the summed-stream <audio> path is used.
   * Defaults to a `(pointer: coarse)` media-query probe. Injected in tests.
   */
  isMobile?: boolean;
}

// Debounce window for mix-mutating bursts (drag a fader, toggle several mutes) —
// coalesce them into a single offline render.
const RENDER_DEBOUNCE_MS = 250;
// Lock-screen scrubber refresh cadence while playing.
const POSITION_TICK_MS = 1000;

function detectMobile(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
}

export interface BackgroundPlaybackHandle {
  /**
   * Call this SYNCHRONOUSLY from the Play control's click handler. On mobile it
   * starts the pre-armed background <audio> inside the user gesture (the only
   * moment WebKit allows it), silences the live engine, and runs the live engine
   * muted for the visual playhead. On desktop it just starts the live player.
   */
  onPlayGesture(): void;
  teardown(): void;
}

export function wireBackgroundPlayback(deps: BackgroundPlaybackDeps): BackgroundPlaybackHandle {
  const isMobile = deps.isMobile ?? detectMobile();

  // Media Session (lock-screen / Control-Center) transport controls.
  // Mobile: drive the background <audio> (the audible source). Desktop: drive
  // the live player. onSeek also moves the live player so the visual playhead
  // stays coherent on mobile.
  deps.wireMediaSession(
    isMobile
      ? {
          // Each of these is a discontinuity: re-anchor the lock screen at once
          // instead of leaving iOS to extrapolate until the next 1s tick.
          onPlay: () => { deps.backgroundAudio.play(); deps.setPlaybackState('playing'); reportPosition(); },
          onPause: () => { deps.backgroundAudio.pause(); deps.setPlaybackState('paused'); reportPosition(); },
          onSeek: (sec: number) => {
            deps.backgroundAudio.seek(sec);
            deps.player.seek(sec);
            reportPosition();
          },
        }
      : {
          onPlay: () => { void deps.player.play(); deps.setPlaybackState('playing'); reportPosition(); },
          onPause: () => { deps.player.pause(); deps.setPlaybackState('paused'); reportPosition(); },
          onSeek: (sec: number) => { deps.player.seek(sec); reportPosition(); },
        },
    deps.metadata,
  );

  // ─── Mobile-only render/arm machinery ───────────────────────────────────
  // True once the background <audio> has been started (via onPlayGesture). It is
  // started muted: the tap is the only moment WebKit will unlock the element, but
  // during the load window the LIVE engine is what the listener hears.
  let bgStarted = false;

  // True once the <audio> has TAKEN OVER as the audible source. Until then the
  // live engine is audible and the element is muted, so the element is not the
  // clock and nothing it does is heard.
  //
  // Why the handoff is deferred (B-260728, the real defect behind "the lock-screen
  // clock jumps by a second, then ten, then a minute"):
  //
  // Playback starts on the FIRST stem (player.ts "start-on-first") and the rest
  // arrive over the network, each emitting 'mixchanged'. Every 'mixchanged' used
  // to trigger a full-song offline re-render and then swap the playing element's
  // src — which pauses it, throws away the decoded audio and re-decodes tens of
  // megabytes of WAV. On a phone that takes real time, so the lock-screen clock
  // froze and then jumped by however long the swap took: a different amount every
  // time. The 250 ms debounce coalesces nothing when stems land seconds apart.
  //
  // So: while stems are still arriving, the element is never the audible source
  // and is never armed under audio. One arm happens, once, at handoff.
  let handedOff = false;

  // Stem-load completeness, from the player's own load-progress counter.
  let mixComplete = false;
  // A stem that fails to load never increments `current`, so `current === total`
  // may never arrive. Progress stalling for this long means "as complete as it
  // is going to get" — hand off rather than leave the lock screen silent.
  const LOAD_STALL_MS = 8000;
  let stallTimer: ReturnType<typeof setTimeout> | null = null;

  // Cheap content key over the mix snapshot — gain/pan per input. When it hasn't
  // changed (and we've armed at least once) we skip the heavy offline re-render:
  // the rendered blob already spans 0→end, so only a MIX change needs a new blob;
  // a position change (play/seek) never does.
  let lastSnapshotKey = '';
  let armedOnce = false;
  function snapshotKey(inputs: MixInput[]): string {
    return inputs.map((i) => `${i.gain}:${i.pan}`).join('|');
  }

  // Overlapping-render guard: at most one render in flight; a request landing
  // mid-render is remembered and re-run once (latest-wins).
  let rendering = false;
  let renderPending = false;

  async function renderAndArm(): Promise<void> {
    if (rendering) {
      renderPending = true;
      return;
    }
    rendering = true;
    try {
      do {
        renderPending = false;
        const snapshot = deps.player.getMixSnapshot();
        lastSnapshotKey = snapshotKey(snapshot);
        const blob = await deps.renderToBlob(snapshot, {
          sampleRate: deps.sampleRate,
          durationSec: deps.player.getDuration(),
        });
        // Once the <audio> is the AUDIBLE source, preserve ITS position across a
        // re-arm; while the live engine is audible, follow the live playhead —
        // that is what the listener is actually hearing.
        const position = handedOff ? deps.backgroundAudio.currentTime() : deps.player.getPlayhead();
        deps.backgroundAudio.arm(blob, position);
        // A re-arm swaps the element's src (which pauses it); if it was the live
        // audible source, resume it (the element is already user-activated, so
        // this programmatic play() is permitted).
        if (bgStarted) deps.backgroundAudio.play();
        armedOnce = true;
        // The src swap above paused the element and reset its clock to `position`
        // while the new blob decodes. Without an immediate report iOS keeps
        // extrapolating from the pre-swap anchor — the handle runs ahead, then the
        // next tick yanks it back. This is the jump in B-260728.
        reportPosition();
      } while (renderPending);
    } catch (err) {
      // S5: a render failure must NEVER break the foreground player. Warn and
      // no-op — the background element keeps its last-armed blob. The guards
      // reset in `finally`, so a later trigger can retry.
      // eslint-disable-next-line no-console
      console.warn('[background-audio] mix render failed', err);
    } finally {
      rendering = false;
    }
  }

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  // Render only when we haven't armed yet (eager first arm) OR the mix CONTENT
  // actually changed. A position change never needs a re-render.
  function scheduleRender(delayMs: number): void {
    if (armedOnce && snapshotKey(deps.player.getMixSnapshot()) === lastSnapshotKey) return;
    // Plan A: once playback is running but the handoff has not happened, stems are
    // still arriving and every one of them would otherwise mean another full-song
    // offline render. Skip them all — `handoff()` renders once, at the end. The
    // eager first arm (before bgStarted) is kept: it is the emergency blob if the
    // screen is locked mid-load, when there is no time left to render anything.
    if (armedOnce && bgStarted && !handedOff) return;
    if (debounceTimer !== null) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      void renderAndArm();
    }, delayMs);
  }

  function clearStallTimer(): void {
    if (stallTimer !== null) { clearTimeout(stallTimer); stallTimer = null; }
  }

  function armStallTimer(): void {
    if (handoffStarted) return;
    clearStallTimer();
    stallTimer = setTimeout(() => { stallTimer = null; void handoff(); }, LOAD_STALL_MS);
  }

  // One-way latch: the handoff runs at most once per session.
  let handoffStarted = false;

  /**
   * Hand the audible role from the live engine to the background <audio>.
   *
   * Renders the finished mix once, waits for the element to be decodable, re-syncs
   * it to wherever the live playhead has reached in the meantime, and only then
   * unmutes it and silences the engine. Doing it in that order is what makes the
   * switch inaudible — the expensive part happens while the listener is still
   * hearing the live engine.
   */
  async function handoff(): Promise<void> {
    if (!isMobile || handoffStarted || !bgStarted) return;
    handoffStarted = true;
    clearStallTimer();
    try {
      // `handedOff` is still false here, so renderAndArm arms at the LIVE playhead
      // — the position the listener is actually at.
      await renderAndArm();
      await deps.backgroundAudio.whenPlayable();
      // Rendering and decoding took real time; the live engine kept going.
      deps.backgroundAudio.seek(deps.player.getPlayhead());
      deps.backgroundAudio.play();
      deps.backgroundAudio.setMuted(false);
      deps.player.setSilent(true);
      handedOff = true;
      reportPosition();      // one last anchor at the switchover…
      stopPeriodicReports(); // …then let WebKit track the element itself
    } catch (err) {
      // A failed handoff must not leave the listener in silence: the live engine
      // is still audible and still the source of truth. Background audio simply
      // will not survive backgrounding this session.
      handoffStarted = false;
      // eslint-disable-next-line no-console
      console.warn('[background-audio] handoff failed', err);
    }
  }

  const unsubscribe = deps.player.subscribe((event) => {
    // Desktop: the live engine is primary and audible; no background render/arm.
    if (!isMobile) return;
    switch (event.type) {
      case 'load-progress':
        // The completeness signal plan A hangs on. A stem that fails to load never
        // increments `current`, so also arm the stall timer on every step.
        if (event.current >= event.total) {
          mixComplete = true;
          void handoff();
        } else {
          armStallTimer();
        }
        break;
      case 'playing':
        // The gesture-critical first start is onPlayGesture(). Here we only keep
        // the blob fresh and, once the element owns the audio, keep the engine
        // silenced across a resume. Before the handoff the engine is the audible
        // source, so silencing it here would mute playback outright.
        scheduleRender(0);
        if (handedOff) {
          deps.player.setSilent(true);
          // A seek is internally pause() + play() (player.ts seek), and a section
          // jump is a seek. We paused the element on that 'paused'; if we do not
          // resume it here it stays paused for good — the lock-screen widget
          // freezes at the old timestamp and goes silent while the in-page player
          // carries on, because the live engine restarts itself. Reproduced
          // against production: one tap on the overview, and every later
          // setPositionState reported the same frozen position.
          deps.backgroundAudio.seek(deps.player.getPlayhead());
          deps.backgroundAudio.play();
          reportPosition();
        }
        deps.setPlaybackState('playing');
        break;
      case 'mixchanged':
        scheduleRender(RENDER_DEBOUNCE_MS);
        break;
      case 'paused':
        // On mobile the <audio> IS the audible source — mirror a UI pause onto
        // it. (There is no frozen-foreground hazard here: the old model's
        // hide-time pause is gone, so a 'paused' always means a real user pause.)
        deps.backgroundAudio.pause();
        deps.setPlaybackState('paused');
        break;
      case 'ended':
        deps.backgroundAudio.pause();
        deps.setPlaybackState('none');
        break;
      default:
        break;
    }
  });

  // Keep the lock-screen scrubber tracking. On mobile the <audio> is the clock;
  // on desktop the live playhead is.
  //
  // B-260728 — the scrubber jumped forward then snapped back on iOS. Two causes,
  // both fixed here:
  //
  // 1. MIXED CLOCKS. Position came from the background <audio> while duration came
  //    from the live player. iOS extrapolates position between our reports against
  //    the duration we gave it, so any disagreement between the two clocks shows up
  //    as the handle drifting and then being yanked back. Both numbers now come
  //    from whichever source is actually audible.
  // 2. REPORTED ONLY ON A 1s TIMER. Every discontinuity — first play, a seek, a
  //    re-arm that pauses the element while a new blob decodes — left iOS
  //    extrapolating from a stale anchor for up to a second. Position is now
  //    reported immediately at each of those moments too; the timer only keeps a
  //    steady stream going.
  function reportPosition(): void {
    // Report from whichever source is AUDIBLE: the live engine during the load
    // window, the element after the handoff. Reporting the element's clock while
    // the engine is the one playing would put a second wrong number on the lock
    // screen — the very class of defect this bug was about.
    if (isMobile && handedOff) {
      // Fall back to the player's duration only while the blob's metadata is still
      // loading (duration() === 0) — an unset duration is worse than a close one.
      const duration = deps.backgroundAudio.duration() || deps.player.getDuration();
      const position = deps.backgroundAudio.currentTime();
      if (!(duration > 0) || position > duration) return;   // Safari rejects such pairs
      deps.setPositionState({ duration, position });
      return;
    }
    if (!deps.player.isPlaying()) return;
    const duration = deps.player.getDuration();
    const position = deps.player.getPlayhead();
    if (!(duration > 0) || position > duration) return;
    deps.setPositionState({ duration, position });
  }

  /**
   * Periodic anchoring, and why it stops on mobile once the element owns the audio.
   *
   * `setPositionState` is for sources the browser cannot see into — Web Audio on
   * desktop. Once the <audio> element is the audible source, WebKit tracks the
   * lock-screen timeline from the element's own clock, which lives in the media
   * pipeline and keeps running while the page is frozen.
   *
   * A 1 Hz timer does NOT keep running there: iOS throttles and then freezes
   * timers in a backgrounded tab. So every wake pushed a position measured
   * whenever JS happened to get a slice, over the top of a clock that had been
   * advancing correctly all along — the handle jumped by however long JS had been
   * asleep. A second, then ten, then a minute, exactly as reported.
   *
   * After the handoff we therefore anchor only on discontinuities — play, pause,
   * seek, the handoff itself — moments when we know the position changed and the
   * element's own clock would otherwise be a step behind.
   */
  let positionTimer: ReturnType<typeof setInterval> | null = setInterval(reportPosition, POSITION_TICK_MS);

  function stopPeriodicReports(): void {
    if (positionTimer !== null) { clearInterval(positionTimer); positionTimer = null; }
  }

  // The screen going off (or the app being switched away) is the deadline: iOS
  // suspends Web Audio, so the live engine cannot carry the audio past this
  // moment. Hand over with whatever is loaded — a partial mix beats silence, and
  // this is the one case where a gap is preferable to nothing.
  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') void handoff();
  };
  if (isMobile) document.addEventListener('visibilitychange', onVisibilityChange);

  return {
    onPlayGesture(): void {
      if (!isMobile) {
        void deps.player.play();
        deps.setPlaybackState('playing');
        return;
      }
      // EVERY Play press comes through here, not just the first
      // (player-view/transport.ts startPlayback). Once the element owns the audio,
      // this is a plain resume — muting it here would silence the background for
      // the rest of the session, since the handoff latch never fires twice. That
      // shipped for twenty minutes and read as "press pause, then play, and there
      // is no sound outside the browser".
      if (handedOff) {
        deps.backgroundAudio.play();
        deps.player.setSilent(true);
        void deps.player.play();          // keeps the visual playhead moving
        deps.setPlaybackState('playing');
        reportPosition();
        return;
      }
      // Start the element MUTED. The tap is the only moment WebKit will unlock it,
      // so it has to run — but the live engine stays audible until the mix is
      // complete, which is what keeps playback smooth while stems arrive.
      deps.backgroundAudio.setMuted(true);
      deps.backgroundAudio.play();
      deps.player.setSilent(false);
      void deps.player.play();
      deps.setPlaybackState('playing');
      bgStarted = true;
      // First anchor for the lock screen (from the live engine — it is what is
      // audible right now).
      reportPosition();
      // Second play of a cached song: everything is already loaded, so there is
      // nothing to wait for and the element can take over immediately.
      if (mixComplete) void handoff();
      else armStallTimer();
    },
    teardown(): void {
      if (debounceTimer !== null) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      clearStallTimer();
      stopPeriodicReports();
      if (isMobile) document.removeEventListener('visibilitychange', onVisibilityChange);
      unsubscribe();
      deps.backgroundAudio.teardown();
    },
  };
}
